import { COPILOT_INITIALIZE_OPEN } from 'chatspot-core/constants/SessionStorageKeys';
import { CopilotChannelContext } from 'copilot-toolkit/channel/CopilotChannelContext';
import { useChatspotLayoutType } from 'copilot-toolkit/channel/hooks/useCopilotLayoutType';
import { useSubscribeToCopilotChannel } from 'copilot-toolkit/channel/hooks/useSubscribeToCopilotChannel';
import { COPILOT_SIDEBAR_CONTAINER_CLASS } from 'copilot-toolkit/constants/LayoutClasses';
import { useTheme } from 'foundations-theming/utils';
import { capabilities } from 'navigation-components/capabilities';
import { useInvokeCapability } from 'navigation-components/consumer/hooks';
import { QUARTZ_SIDEBAR_CONTAINER_CLASS } from 'quartz/grid';
import { useCallback, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { useIsUngatedForUIRefresh } from '../gating/hooks/useIsUngatedForUIRefresh';
import { useIsCopilotEnabled } from '../hooks/useIsCopilotEnabled';
import { useHostAppContext } from '../hostAppContext/hooks/useAppContext';
import { useIsDisabledPath } from '../threads/hooks/useOnParentPathnameChange';
import { calculateGlobalStyles, calculateIframesStyles } from './calculateStyles';
// '0' = closed | '1' = open | '2' = open expanded view
const setOpenSessionStorage = isExpanded => {
  try {
    if (isExpanded) {
      sessionStorage.setItem(COPILOT_INITIALIZE_OPEN, '2');
    } else {
      sessionStorage.setItem(COPILOT_INITIALIZE_OPEN, '1');
    }
  } catch (e) {
    // ignore
  }
};
const clearOpenSessionStorage = () => {
  try {
    sessionStorage.setItem(COPILOT_INITIALIZE_OPEN, '0');
  } catch (e) {
    // ignore
  }
};
const SetupNavigationCapability = ({
  copilotLayoutType,
  isOpen,
  isSearchOpen
}) => {
  const isPathDisabled = useIsDisabledPath();
  const layoutTypeFromChannel = useChatspotLayoutType();
  const layoutType = copilotLayoutType || layoutTypeFromChannel;
  const isCopilotEnabled = useIsCopilotEnabled();
  const theme = useTheme();
  const isUngatedForUIRefresh = useIsUngatedForUIRefresh();
  const {
    channel
  } = useContext(CopilotChannelContext);
  const {
    isCopilotExpanded,
    setIsCopilotExpanded,
    isFileManagerOpen
  } = useHostAppContext();
  const previousOpenState = useRef(isOpen);
  const isInitialOpen = useRef(isOpen);
  const closedWhileExpanded = useRef(false);
  const updateGlobalStyles = useInvokeCapability(capabilities.UPSERT_GLOBAL_STYLES_CAP);
  const updateIframeStyles = useInvokeCapability(capabilities.RESTYLE_CAP);
  const showWidget = useInvokeCapability(capabilities.SHOW_COMPONENT_CAP);
  const hideWidget = useInvokeCapability(capabilities.HIDE_COMPONENT_CAP);
  const onOpenWidget = useCallback(() => {
    showWidget();
  }, [showWidget]);
  const onCloseWidget = useCallback(() => {
    hideWidget();
  }, [hideWidget]);

  // Upon mounting check if Copilot is both open and previously expanded => true => open expanded state
  useEffect(() => {
    if (isOpen && sessionStorage.getItem(COPILOT_INITIALIZE_OPEN) === '2') {
      setIsCopilotExpanded(true, {
        skipTracking: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (isOpen && isCopilotEnabled) {
      if (isCopilotExpanded) {
        setOpenSessionStorage(true);
      } else {
        setOpenSessionStorage(false);
      }
    } else {
      clearOpenSessionStorage();
    }
  }, [isOpen, isCopilotEnabled, isCopilotExpanded]);
  useEffect(() => {
    if (channel) {
      channel.setState({
        isWidgetOpen: isOpen
      });
    }
  }, [channel, isOpen]);
  useEffect(() => {
    if (channel && !isPathDisabled) {
      channel.setState({
        isNavCopilotAvailable: true
      });
    }
    return () => {
      if (channel && !isPathDisabled) {
        channel.setState({
          isNavCopilotAvailable: false
        });
      }
    };
  }, [channel, isPathDisabled]);
  useSubscribeToCopilotChannel('SEND_ACTION', onOpenWidget);
  useSubscribeToCopilotChannel('SEND_MESSAGE', onOpenWidget);
  useSubscribeToCopilotChannel('LAUNCH_FROM_COPILOT_PREVIEW', onOpenWidget);
  useSubscribeToCopilotChannel('LAUNCH_WITH_CUSTOM_COPILOT', onOpenWidget);
  useSubscribeToCopilotChannel('OPEN_PROMPT_TEMPLATE', onOpenWidget);
  useSubscribeToCopilotChannel('PROMPT_USER_FOR_MORE_INFO', onOpenWidget);
  useSubscribeToCopilotChannel('OPEN_COPILOT', onOpenWidget);
  useSubscribeToCopilotChannel('CLOSE_COPILOT', onCloseWidget);
  useLayoutEffect(() => {
    const isClosing = !isOpen && previousOpenState.current;
    const isOpening = isOpen && previousOpenState.current === false;
    updateIframeStyles({
      style: calculateIframesStyles({
        isCopilotExpanded,
        isOpen,
        layoutType,
        previousOpenState: previousOpenState.current,
        skipOpenAnimation: isSearchOpen || false,
        isFileManagerOpen,
        theme,
        isUngatedForUIRefresh
      }),
      merge: true
    });
    if (isOpening) {
      closedWhileExpanded.current = false;
    }
    if (isClosing) {
      closedWhileExpanded.current = isCopilotExpanded;
      setIsCopilotExpanded(false, {
        skipTracking: true
      });
    }

    // Important that this happens after the style calculation so we get the
    // correct previous open state
    previousOpenState.current = isOpen;
  }, [isCopilotExpanded, isFileManagerOpen, layoutType, isOpen, isSearchOpen, setIsCopilotExpanded, updateIframeStyles, theme, isUngatedForUIRefresh]);
  useLayoutEffect(() => {
    const newGlobalStyles = calculateGlobalStyles({
      isOpen,
      layoutType,
      // There are also accompanying styles in renderWithNavCopilot in
      // copilot-toolkit that make this animation skipping possible for the
      // isInitialOpen case
      skipOpenAnimation: isSearchOpen || isInitialOpen.current || closedWhileExpanded.current
    });
    updateGlobalStyles({
      selector: `.${COPILOT_SIDEBAR_CONTAINER_CLASS}`,
      style: newGlobalStyles
    });
    updateGlobalStyles({
      selector: `.${QUARTZ_SIDEBAR_CONTAINER_CLASS}`,
      style: newGlobalStyles
    });

    /**
     * This is a hack to fix the UIModal causing the Copilot sidebar to shift
     * down by the nav height.
     *
     * This can probably be removed after the Trellis migration is complete.
     */
    updateGlobalStyles({
      selector: `.uiFullScreenBackground [name="nav-components:chatspot-sidebar"]`,
      style: {
        top: '0 !important'
      }
    });
    isInitialOpen.current = false;
  }, [layoutType, isOpen, isSearchOpen, updateGlobalStyles]);
  return null;
};
export default SetupNavigationCapability;